import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useMemo } from 'react';
import { captureException } from '@/modules/sentry';
import type {
  IAuthLogoutOptions,
  TGetAccessToken,
  TLoginWithRedirect,
  TLogout,
} from '../types';

export interface IAuthUser {
  sub: string;
  email?: string;
  name?: string;
  family_name?: string;
  given_name?: string;
  nickname?: string;
  picture?: string;
  email_verified?: boolean;
  [key: string]: unknown;
}

export interface IUseAuthReturn {
  user?: IAuthUser;
  isAuthenticated: boolean;
  isLoading: boolean;
  error?: Error;
  loginWithRedirect: TLoginWithRedirect;
  logout: TLogout;
  getAccessTokenSilently: TGetAccessToken;
}

export const useAuth = (): IUseAuthReturn => {
  const {
    user,
    isAuthenticated,
    isLoading,
    error,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently,
  } = useAuth0();

  const logout = useCallback(
    (options?: IAuthLogoutOptions) => {
      // TLogout is void-returning, so a rejected logout has no caller to handle
      // it — report it here or the user just sits on the logout spinner.
      auth0Logout({
        logoutParams: {
          returnTo: `${window.location.origin}/login`,
          ...options?.logoutParams,
        },
      }).catch(logoutError => {
        captureException(logoutError, {
          tags: { module: 'auth', action: 'logout' },
        });
      });
    },
    [auth0Logout],
  );

  const authUser = useMemo(() => {
    if (!user) return undefined;

    return {
      sub: user.sub || '',
      email: user.email,
      name: user.name,
      picture: user.picture,
      email_verified: user.email_verified,
      ...user,
    } as IAuthUser;
  }, [user]);

  return {
    user: authUser,
    isAuthenticated,
    isLoading,
    error,
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
  };
};
